import { z } from "zod";
import { labCodeSchema } from "./labs.js";
/* Lab result ingest: the shape a lab's submission has *once it is ours*.
 *
 * This is deliberately not anyone's wire format. Whatever Arvoterveys ends up
 * pushing at us gets translated into these types by an adapter, and the
 * adapter is then the only place that knows what they call things — see
 * platform/docs/lab-result-ingest.md, "The seam". The CLI in staging builds
 * these directly, so the same core is exercised either way.
 *
 * Nothing here carries a reference range, and nothing may: a submission is
 * measured values and the facts about where they came from. Root CLAUDE.md's
 * "the software does not interpret", REQ-MDR-002.
 */
const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const externalIdSchema = z.string().min(1).max(128);
/* Who the submission is about. A union rather than one field because we do not
 * yet know which key Arvo will send (undecided, Ville 2026-09-08) and all
 * three are cheap to support:
 *
 *  - `order_number` — our own FE- series, which the client already quotes at
 *    the laboratory (`GET /commerce/me/orders` exists to hand it to them).
 *  - `national_id` — a HETU, which is how a lab actually identifies a patient.
 *    Hashed at the ingest boundary and never persisted in plaintext; see
 *    docs/auth.md's hash-only rule and ingest/redact.ts.
 *  - `user_id` — our own id. No real lab will send this; it is what the
 *    staging CLI uses when it already knows who it means.
 *
 * A fourth key (an email, say) is an adapter change plus a resolver arm, not a
 * reshaping of this type.
 */
export const subjectRefSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("order_number"), value: z.string().min(1).max(64) }),
    z.object({ kind: z.literal("national_id"), value: z.string().min(6).max(32) }),
    z.object({ kind: z.literal("user_id"), value: z.string().uuid() }),
]);
export const collectionMethodSchema = z.enum(["finger_prick", "venous"]);
/* Facts about the draw itself, shared by both submission kinds.
 *
 * `collectionMethod` and `collectedOn` are required on *both* — including on a
 * result that arrives with no prior draw notification, which is today's normal
 * case (product/flows/arvo_terveys_lab_coordination.md: the nurse submits once,
 * at the end). They are `not null` on `specimens`, and defaulting them would
 * be the platform inventing a clinical fact. If a lab cannot tell us how a
 * sample was taken, that is an adapter-time conversation, not a fallback.
 *
 * `externalRef` is the lab's own sample id: the handle that lets a
 * `specimen_collected` notification and the `result_reported` that follows it
 * land on one specimen row. Nullable because the staging CLI may not bother
 * and a single-submission lab has no need of it.
 */
const specimenFactsSchema = z.object({
    externalRef: externalIdSchema.nullable().default(null),
    collectedOn: dateOnlySchema,
    collectionMethod: collectionMethodSchema,
    collectingOrg: z.string().max(200).nullable().default(null),
    panelCode: z.string().max(64).nullable().default(null),
});
const submissionEnvelopeSchema = z.object({
    labCode: labCodeSchema,
    /** The sender's own id for this submission — the outermost idempotency key.
        Unique per lab; a redelivery under the same id is recognised and does
        nothing. */
    externalId: externalIdSchema,
    subject: subjectRefSchema,
});
/** A draw happened. The notification we have never had — `journey/spine.ts`
    records the `sampled` stage as unreachable for exactly this reason — and one
    insert to fix. */
export const specimenCollectedSubmissionSchema = submissionEnvelopeSchema
    .merge(specimenFactsSchema)
    .extend({ kind: z.literal("specimen_collected") });
/* Values are in.
 *
 * `analyteCode` is a plain string, not `labAnalyteCodeSchema`'s enum, and that
 * is the whole point: a lab reporting a twelfth analyte we have not catalogued
 * must not lose us the eleven we do know. Ingest stores the known ones, keeps
 * the payload, and lands the receipt as `stored_partial` naming what it
 * skipped. Rejecting the draw would be the worse failure.
 *
 * `unit` is required and is *checked*, never converted — see
 * ingest/validate.ts. A magnitude conversion would silently rewrite a clinical
 * number with nothing in the record saying we did (Ville, 2026-09-08: reject
 * for now).
 */
export const resultReportedSubmissionSchema = submissionEnvelopeSchema
    .merge(specimenFactsSchema)
    .extend({
    kind: z.literal("result_reported"),
    reportedAt: z.string().datetime(),
    sourceLab: z.string().max(200).nullable().default(null),
    analytes: z
        .array(z.object({
        analyteCode: z.string().min(1).max(64),
        value: z.number().finite().positive(),
        unit: z.string().min(1).max(32),
    }))
        .min(1)
        .max(64)
        .superRefine((entries, ctx) => {
        const seen = new Set();
        for (const [i, entry] of entries.entries()) {
            if (seen.has(entry.analyteCode)) {
                // Two values for one analyte in one submission is ambiguous, and
                // picking either would be the platform deciding which measurement
                // of someone's blood counts. Refuse the submission instead.
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, "analyteCode"],
                    message: "analyteCode repeated within one submission",
                });
            }
            seen.add(entry.analyteCode);
        }
    }),
});
export const labSubmissionSchema = z.discriminatedUnion("kind", [
    specimenCollectedSubmissionSchema,
    resultReportedSubmissionSchema,
]);
/* What happened to one call.
 *
 * Deliberately not the same thing as the receipt's `status`: `duplicate` says
 * this *call* found a submission already dealt with, while the stored row keeps
 * whatever terminal status it earned the first time. A caller (the CLI now, a
 * route later) reports on the call; the table reports on the submission.
 */
export const labIngestStatusSchema = z.enum([
    "stored",
    "stored_partial",
    "unmatched",
    "rejected",
    "duplicate",
]);
